import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Lock, RefreshCw, Copy, ImagePlus, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WriterPage() {
  const [pin, setPin] = useState(sessionStorage.getItem("pre_pin") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("enquiries");
  const [leads, setLeads] = useState(null);
  const [form, setForm] = useState({ property_type: "Residential Plot", name: "", location: "Dholera Smart City, Gujarat", sizes: "", price: "", highlights: "" });
  const [draft, setDraft] = useState("");
  const [img, setImg] = useState(null);
  const [busy, setBusy] = useState(false);

  const headers = { "X-Admin-Pin": pin };

  const unlock = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers });
      setLeads(res.data);
      setAuthed(true);
      sessionStorage.setItem("pre_pin", pin);
    } catch {
      toast.error("Wrong PIN");
    }
  };

  const loadLeads = async () => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/admin/leads`, { headers });
      setLeads(res.data);
    } catch {
      toast.error("Could not load enquiries");
    } finally {
      setBusy(false);
    }
  };

  const generateDraft = async () => {
    setBusy(true);
    setDraft("");
    try {
      const res = await axios.post(`${API}/admin/draft`, form, { headers });
      setDraft(res.data.draft);
      toast.success("Draft ready");
    } catch {
      toast.error("Could not generate draft — try again");
    } finally {
      setBusy(false);
    }
  };

  const generateImage = async () => {
    setBusy(true);
    setImg(null);
    try {
      const prompt = `${form.property_type} ${form.name} in ${form.location}. ${form.highlights}`.slice(0, 500);
      const res = await axios.post(`${API}/admin/draft-image`, { prompt }, { headers, timeout: 120000 });
      setImg(`data:${res.data.mime_type};base64,${res.data.image_base64}`);
      toast.success("Illustrative image ready");
    } catch {
      toast.error("Could not generate image — try again");
    } finally {
      setBusy(false);
    }
  };

  const copyDraft = () => {
    navigator.clipboard.writeText(draft);
    toast.success("Draft copied");
  };

  if (!authed) {
    return (
      <main className="min-h-screen bg-navy flex items-center justify-center px-5" data-testid="writer-gate">
        <form onSubmit={unlock} className="w-full max-w-sm bg-white rounded-sm shadow-deep p-8">
          <Lock className="w-8 h-8 text-gold" />
          <h1 className="mt-4 font-serif text-3xl text-navy font-semibold">Owner Access</h1>
          <p className="mt-2 text-body text-sm">Enter the owner PIN to view enquiries with AI summaries and use the AI content writer.</p>
          <input
            type="password"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            placeholder="Owner PIN"
            data-testid="writer-pin-input"
            className="mt-5 w-full bg-paper border border-border px-4 py-3 text-sm outline-none focus:border-gold transition-colors duration-300"
          />
          <button type="submit" data-testid="writer-unlock-button" className="btn-navy w-full mt-4">Unlock</button>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 text-sm text-navy/60 hover:text-gold-dark transition-colors duration-300">
            <ArrowLeft className="w-4 h-4" /> Back to website
          </Link>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-paper" data-testid="writer-page">
      <header className="bg-navy pt-10 pb-8">
        <div className="container-luxe flex flex-wrap items-center justify-between gap-4">
          <div>
            <Link to="/" className="inline-flex items-center gap-2 text-gold text-sm hover:text-white transition-colors duration-300" data-testid="writer-back-home">
              <ArrowLeft className="w-4 h-4" /> Back to website
            </Link>
            <h1 className="mt-3 font-serif text-4xl text-white font-semibold">Owner Studio</h1>
            <p className="text-white/55 text-sm mt-1">AI summaries of enquiries + AI content writer</p>
          </div>
          <div className="flex gap-2">
            {["enquiries", "writer"].map((t) => (
              <button
                key={t}
                onClick={() => setTab(t)}
                data-testid={`writer-tab-${t}`}
                className={`px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${tab === t ? "bg-gold text-navy" : "text-white/70 border border-white/20 hover:border-gold"}`}
              >
                {t === "enquiries" ? "Enquiries" : "Content Writer"}
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="container-luxe py-10">
        {tab === "enquiries" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl text-navy font-semibold">Recent Enquiries</h2>
              <button onClick={loadLeads} data-testid="writer-refresh-leads" className="inline-flex items-center gap-2 text-sm font-semibold text-navy border border-navy/20 px-4 py-2 hover:border-gold transition-colors duration-300">
                <RefreshCw className="w-4 h-4" /> Refresh
              </button>
            </div>
            {!leads || leads.length === 0 ? (
              <p className="text-body text-sm bg-white border border-border p-8 text-center">No enquiries yet. New leads from the website will appear here with an AI summary.</p>
            ) : (
              <div className="space-y-4">
                {leads.map((l) => (
                  <div key={l.id} className="bg-white border border-border shadow-card p-6 rounded-sm" data-testid="writer-lead-card">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-serif text-xl text-navy font-semibold">{l.name}</p>
                        <p className="text-sm text-body">+91 {l.phone.slice(-10)} · {l.property_type}{l.budget ? ` · ${l.budget}` : ""}</p>
                        {l.email && <p className="text-sm text-body">{l.email}</p>}
                      </div>
                      <p className="font-mono text-[11px] text-navy/45">{new Date(l.created_at).toLocaleString("en-IN")}</p>
                    </div>
                    {l.message && <p className="mt-3 text-sm text-body border-l-2 border-border pl-3">{l.message}</p>}
                    <div className="mt-4 bg-gold-light border border-gold/40 px-4 py-3">
                      <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold-dark mb-1">AI Summary</p>
                      <p className="text-sm text-navy whitespace-pre-line">{l.ai_summary || "Summary is being generated — refresh in a moment."}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "writer" && (
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="bg-white border border-border shadow-card p-7 rounded-sm space-y-4">
              <h2 className="font-serif text-2xl text-navy font-semibold flex items-center gap-2"><Sparkles className="w-5 h-5 text-gold" /> Draft a Listing</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-navy/60 mb-1.5">Property Type</label>
                  <select data-testid="writer-input-type" className="w-full bg-paper border border-border px-3.5 py-3 text-sm outline-none focus:border-gold" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })}>
                    {["Residential Plot", "Commercial Plot", "Industrial Land", "Investment Land"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-navy/60 mb-1.5">Project / Listing Name</label>
                  <input data-testid="writer-input-name" className="w-full bg-paper border border-border px-3.5 py-3 text-sm outline-none focus:border-gold" placeholder="e.g. Highway frontage plots" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-navy/60 mb-1.5">Location</label>
                  <input data-testid="writer-input-location" className="w-full bg-paper border border-border px-3.5 py-3 text-sm outline-none focus:border-gold" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
                </div>
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-navy/60 mb-1.5">Plot Sizes</label>
                  <input data-testid="writer-input-sizes" className="w-full bg-paper border border-border px-3.5 py-3 text-sm outline-none focus:border-gold" placeholder="e.g. 100–500 sq yd" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-navy/60 mb-1.5">Price (leave blank = on request)</label>
                  <input data-testid="writer-input-price" className="w-full bg-paper border border-border px-3.5 py-3 text-sm outline-none focus:border-gold" placeholder="e.g. Starting ₹XX lakh" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-navy/60 mb-1.5">Key Highlights</label>
                  <textarea data-testid="writer-input-highlights" rows="3" className="w-full bg-paper border border-border px-3.5 py-3 text-sm outline-none focus:border-gold resize-none" placeholder="e.g. Near expressway exit, clear title guidance, wide road" value={form.highlights} onChange={(e) => setForm({ ...form, highlights: e.target.value })} />
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-2">
                <button onClick={generateDraft} disabled={busy} data-testid="writer-generate-draft" className="btn-gold disabled:opacity-60">{busy ? "Working…" : "Generate Draft (Claude)"}</button>
                <button onClick={generateImage} disabled={busy} data-testid="writer-generate-image" className="btn-navy disabled:opacity-60"><ImagePlus className="w-4 h-4" /> Illustrative Image (Gemini)</button>
              </div>
              <p className="text-xs text-navy/45">Drafts and images are AI-generated — review before publishing anywhere. Images are illustrative, not real photos.</p>
            </div>

            <div className="space-y-6">
              {draft && (
                <div className="bg-white border border-border shadow-card p-7 rounded-sm" data-testid="writer-draft-output">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-serif text-xl text-navy font-semibold">Draft</h3>
                    <button onClick={copyDraft} data-testid="writer-copy-draft" className="inline-flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold-dark transition-colors duration-300"><Copy className="w-4 h-4" /> Copy</button>
                  </div>
                  <pre className="whitespace-pre-wrap text-sm text-body leading-relaxed font-sans">{draft}</pre>
                </div>
              )}
              {img && (
                <div className="bg-white border border-border shadow-card p-4 rounded-sm" data-testid="writer-image-output">
                  <img src={img} alt="AI-generated illustrative property visual" className="w-full rounded-sm" />
                  <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-gold-dark text-center">AI-generated illustrative visual — not a real photo</p>
                </div>
              )}
              {!draft && !img && (
                <div className="border border-dashed border-navy/20 p-10 text-center text-navy/40 text-sm">
                  Your AI draft and illustrative image will appear here.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
